/**
 * Chat activity client registry
 *
 * Shared browser-side storage for generic chat completion status.
 *
 * Primary responsibilities:
 * - Keep a per-chat activity status registry in localStorage
 * - Share loader and unread-completion state across tabs
 * - Emit same-tab change events after localStorage writes
 *
 * Dependencies:
 * - types/chat
 */

'use client'

import type { ChatActivityStatus } from '@/types/chat'

export const CHAT_ACTIVITY_REGISTRY_STORAGE_KEY = 'chat.activity.registry'
export const CHAT_ACTIVITY_REGISTRY_EVENT = 'chat-activity-registry-changed'
export const CHAT_LATEST_SCROLL_REQUESTS_STORAGE_KEY = 'chat.latest-scroll.requests'
export const CHAT_LATEST_SCROLL_REQUEST_EVENT = 'chat-latest-scroll-requested'

type StoredChatActivityRegistry = Record<string, ChatActivityStatus>
type StoredChatIdList = string[]

function canUseBrowserStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function dispatchRegistryChange() {
  if (typeof window === 'undefined') return

  window.dispatchEvent(new CustomEvent(CHAT_ACTIVITY_REGISTRY_EVENT))
}

function readJsonFromStorage<T>(storageKey: string, fallback: T): T {
  if (!canUseBrowserStorage()) return fallback

  try {
    const rawValue = window.localStorage.getItem(storageKey)
    if (!rawValue) return fallback

    return JSON.parse(rawValue) as T
  } catch {
    return fallback
  }
}

function writeJsonToStorage(storageKey: string, value: unknown) {
  if (!canUseBrowserStorage()) return

  window.localStorage.setItem(storageKey, JSON.stringify(value))
}

function readStoredChatIds(storageKey: string): Set<string> {
  const chatIds = readJsonFromStorage<StoredChatIdList>(storageKey, [])

  return new Set(
    Array.isArray(chatIds)
      ? chatIds.filter(
          (value): value is string =>
            typeof value === 'string' && value.trim().length > 0
        )
      : []
  )
}

function writeStoredChatIds(storageKey: string, chatIds: Set<string>) {
  writeJsonToStorage(storageKey, Array.from(chatIds))
}

function isChatActivityStatus(value: unknown): value is ChatActivityStatus {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const record = value as Record<string, unknown>

  return (
    typeof record.chatId === 'string' &&
    typeof record.requestId === 'string' &&
    typeof record.status === 'string' &&
    typeof record.updatedAt === 'number' &&
    typeof record.unread === 'boolean'
  )
}

function isTerminalStatus(status: ChatActivityStatus['status']): boolean {
  return status === 'completed' || status === 'error'
}

export function readChatActivityRegistry(): Record<string, ChatActivityStatus> {
  const registry = readJsonFromStorage<StoredChatActivityRegistry>(
    CHAT_ACTIVITY_REGISTRY_STORAGE_KEY,
    {}
  )

  return Object.fromEntries(
    Object.entries(registry).filter(([, value]) => isChatActivityStatus(value))
  )
}

export function writeChatActivityRegistry(
  registry: Record<string, ChatActivityStatus>
) {
  writeJsonToStorage(CHAT_ACTIVITY_REGISTRY_STORAGE_KEY, registry)
  dispatchRegistryChange()
}

export function createChatActivityStatus(
  activity: Pick<
    ChatActivityStatus,
    'chatId' | 'requestId' | 'status' | 'completedAt'
  >,
  options?: {
    unread?: boolean
    now?: number
  }
): ChatActivityStatus {
  return {
    chatId: activity.chatId,
    requestId: activity.requestId,
    status: activity.completedAt ? 'completed' : activity.status,
    updatedAt: options?.now ?? Date.now(),
    completedAt: activity.completedAt,
    unread: options?.unread ?? false
  }
}

export function mergeChatActivityStatus(
  current: ChatActivityStatus | undefined,
  incoming: ChatActivityStatus
): ChatActivityStatus {
  if (!current) return incoming

  const currentIsTerminal = isTerminalStatus(current.status)
  const incomingIsTerminal = isTerminalStatus(incoming.status)
  const shouldKeepCurrent =
    current.requestId === incoming.requestId &&
    currentIsTerminal &&
    !incomingIsTerminal

  if (shouldKeepCurrent) {
    return current
  }

  if (
    current.updatedAt > incoming.updatedAt &&
    current.requestId !== incoming.requestId
  ) {
    return current
  }

  return {
    ...current,
    ...incoming,
    status:
      current.requestId === incoming.requestId &&
      currentIsTerminal &&
      incomingIsTerminal
        ? current.completedAt && incoming.completedAt
          ? current.completedAt > incoming.completedAt
            ? current.status
            : incoming.status
          : incoming.status
        : incoming.status,
    unread: incoming.unread ?? current.unread,
    updatedAt: Math.max(current.updatedAt, incoming.updatedAt),
    completedAt:
      current.completedAt && incoming.completedAt
        ? Math.max(current.completedAt, incoming.completedAt)
        : incoming.completedAt ?? current.completedAt
  }
}

export function upsertChatActivityStatus(
  registry: Record<string, ChatActivityStatus>,
  incoming: ChatActivityStatus
): Record<string, ChatActivityStatus> {
  return {
    ...registry,
    [incoming.chatId]: mergeChatActivityStatus(registry[incoming.chatId], incoming)
  }
}

export function markChatActivitySeen(
  chatId: string,
  options?: { requestId?: string }
): Record<string, ChatActivityStatus> {
  const registry = readChatActivityRegistry()
  const current = registry[chatId]

  if (!current) return registry
  if (options?.requestId && current.requestId !== options.requestId) return registry
  if (!current.unread) return registry

  const nextRegistry = {
    ...registry,
    [chatId]: {
      ...current,
      unread: false
    }
  }

  writeChatActivityRegistry(nextRegistry)

  return nextRegistry
}

export function clearChatActivityStatus(
  chatId: string,
  options?: { requestId?: string }
): Record<string, ChatActivityStatus> {
  const registry = readChatActivityRegistry()
  const current = registry[chatId]

  if (!current) return registry
  if (options?.requestId && current.requestId !== options.requestId) return registry

  const nextRegistry = { ...registry }
  delete nextRegistry[chatId]

  writeChatActivityRegistry(nextRegistry)

  return nextRegistry
}

export function requestChatLatestScroll(chatId: string) {
  const chatIds = readStoredChatIds(CHAT_LATEST_SCROLL_REQUESTS_STORAGE_KEY)
  chatIds.add(chatId)
  writeStoredChatIds(CHAT_LATEST_SCROLL_REQUESTS_STORAGE_KEY, chatIds)

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(CHAT_LATEST_SCROLL_REQUEST_EVENT, {
        detail: { chatId }
      })
    )
  }
}

export function consumeChatLatestScrollRequest(chatId: string): boolean {
  const chatIds = readStoredChatIds(CHAT_LATEST_SCROLL_REQUESTS_STORAGE_KEY)

  if (!chatIds.has(chatId)) return false

  chatIds.delete(chatId)
  writeStoredChatIds(CHAT_LATEST_SCROLL_REQUESTS_STORAGE_KEY, chatIds)

  return true
}
